// A word-level diff for U5's tone and length rewrites: what a rewrite keeps, adds and removes, so
// the owner can see the change before accepting it. Words and the whitespace after them are one
// token; the diff is the longest common subsequence of tokens. Pure and dependency-free.

export type DiffKind = "same" | "added" | "removed";

export interface DiffPart {
  kind: DiffKind;
  text: string;
}

/** Above this many token pairs the diff is coarse: all of the old text out, all of the new in. */
const MAX_CELLS = 600_000;

/** Words with their trailing whitespace; leading whitespace is its own token. */
export function tokens(text: string): string[] {
  return text.match(/\s+|\S+\s*/g) ?? [];
}

/** Compares words without the whitespace around them (a reflowed line is not a change). */
const key = (t: string) => t.trim();

function push(out: DiffPart[], kind: DiffKind, text: string): void {
  if (!text) return;
  const last = out[out.length - 1];
  if (last && last.kind === kind) last.text += text;
  else out.push({ kind, text });
}

function lcsTable(a: string[], b: string[]): Uint32Array {
  const w = b.length + 1;
  const table = new Uint32Array((a.length + 1) * w);
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      table[i * w + j] =
        key(a[i]!) === key(b[j]!)
          ? table[(i + 1) * w + j + 1]! + 1
          : Math.max(table[(i + 1) * w + j]!, table[i * w + j + 1]!);
    }
  }
  return table;
}

/** The parts of `after` against `before`, in reading order (removed before added at a change). */
export function wordDiff(before: string, after: string): DiffPart[] {
  const a = tokens(before);
  const b = tokens(after);
  const out: DiffPart[] = [];
  if (a.length * b.length > MAX_CELLS) {
    push(out, "removed", before);
    push(out, "added", after);
    return out;
  }
  const table = lcsTable(a, b);
  const w = b.length + 1;
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (key(a[i]!) === key(b[j]!)) {
      push(out, "same", b[j]!);
      i++;
      j++;
    } else if (table[(i + 1) * w + j]! >= table[i * w + j + 1]!) {
      push(out, "removed", a[i++]!);
    } else {
      push(out, "added", b[j++]!);
    }
  }
  while (i < a.length) push(out, "removed", a[i++]!);
  while (j < b.length) push(out, "added", b[j++]!);
  return out;
}

/** How many words a diff adds and removes (for a one-line summary, never a score). */
export function diffSize(parts: readonly DiffPart[]): { added: number; removed: number } {
  const words = (s: string) => s.split(/\s+/).filter(Boolean).length;
  let added = 0;
  let removed = 0;
  for (const p of parts) {
    if (p.kind === "added") added += words(p.text);
    if (p.kind === "removed") removed += words(p.text);
  }
  return { added, removed };
}
